import React, { useState, useEffect } from "react";
import { useParams, useHistory, Redirect } from "react-router-dom";

import { useSnackbar } from "notistack";

import {
  makeStyles,
  Container,
  Paper,
  Typography,
  IconButton,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import CallMadeIcon from "@material-ui/icons/CallMade";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

import useExploreLink from "../hooks/useExploreLink";

import { getNFTInfo, redeemNFT, redeemShare } from "./NFTFunctions";
import LoadingButton from "../Components/LoadingButton";

const useStyles = makeStyles((theme) => ({
  paperContainer: {
    borderRadius: theme.spacing(2),
    width: "100%",
    padding: theme.spacing(2),
    display: "flex",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexFlow: "column",
    },
  },

  imageBox: {
    width: "400px",
    height: "400px",
    order: "1",
    marginLeft: theme.spacing(2),

    [theme.breakpoints.down("md")]: {
      width: "350px",
      height: "350px",
    },

    [theme.breakpoints.down("sm")]: {
      order: "0",
      height: "300px",
      width: "100%",
      marginLeft: "0",
      marginBottom: theme.spacing(2),
    },
  },

  skeletonImage: {
    borderRadius: theme.spacing(2),
  },

  infoLoadingBTNBox: {
    margin: "0px -8px",
    width: "100%",
  },

  nftImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: theme.spacing(2),
  },

  infoBox: {
    flex: "1",
    order: "0",
    display: "flex",
    flexFlow: "column",
    [theme.breakpoints.down("sm")]: {
      order: "1",
    },
  },

  successStatus: {
    color: theme.palette.success.main,
  },
  dangerStatus: {
    color: theme.palette.error.main,
  },

  buttonIconBox: {
    background: theme.palette.background.default,
    width: "32px",
    height: "32px",
    borderRadius: "12px",
    cursor: "pointer",
    marginBottom: theme.spacing(1),
  },
  buttonIconSVG: {
    fontSize: "14px",
  },

  moreInfo: {
    color: "inherit",
    textDecoration: "none",
    "&:hover": {
      color: `${theme.palette.primary.main} !important`,
    },
  },

  exploreIcon: {
    fontSize: "inherit",
    marginLeft: theme.spacing(1),
  },
}));

function NFTPoolDetail({ network }) {
  const history = useHistory();
  let { contract } = useParams();
  const classes = useStyles();

  const { enqueueSnackbar } = useSnackbar();

  const [mainLoading, setMainLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [nftInfo, setNftInfo] = useState({});

  const [convertExploreLink] = useExploreLink();

  const get_nft_info = async () => {
    setMainLoading(true);

    await getNFTInfo(contract, network.account, network.signer).then((res) => {
      if (res) {
        setNftInfo(res);
        console.log(res);
      }
    });

    setMainLoading(false);
  };

  const redeem = async () => {
    console.log("Attempting to Redeem nft...");
    setLoading(true);

    await redeemShare(contract, network.signer)
      .then(() => {
        setLoading(false);

        // If the transaction was successful, we clear to input to make sure the user doesn't accidental redo the transfer
        enqueueSnackbar("Redeem Share Successful", {
          variant: "success",
          autoHideDuration: 5000,
        });
      })
      .catch((e) => {
        setLoading(false);
        enqueueSnackbar("Redeem Share Failed (" + e.reason + ")", {
          variant: "error",
          autoHideDuration: 5000,
        });
      });
  };

  const buyNFT = async () => {
    console.log("Attempting to Trade nft...");
    setLoading(true);

    await redeemNFT(
      network.wethAddress,
      `${nftInfo.finalNftEthPrice}`,
      contract,
      network.signer
    )
      .then(() => {
        setLoading(false);

        // If the transaction was successful, we clear to input to make sure the user doesn't accidental redo the transfer
        enqueueSnackbar("Redeem NFT Successful", {
          variant: "success",
          autoHideDuration: 5000,
        });
      })
      .catch((e) => {
        setLoading(false);
        enqueueSnackbar("Redeem NFT Failed (" + e.reason + ")", {
          variant: "error",
          autoHideDuration: 5000,
        });
      });
  };

  useEffect(() => {
    const myNFTTimeout = setTimeout(() => {
      if (network.stacking) {
        get_nft_info();
      }
    }, 1000);
    return () => clearTimeout(myNFTTimeout);
  }, []);
  return (
    <>
      {network.chainID != 56 ? (
        <div id="my-nft-detail">
          <Container maxWidth="xl">
            <Paper elevation={0} className={classes.paperContainer}>
              <div className={classes.infoBox}>
                <div>
                  <IconButton
                    onClick={() => history.push("/nft-pools")}
                    variant="contained"
                    className={classes.buttonIconBox}
                  >
                    <ArrowBackIcon className={classes.buttonIconSVG} />
                  </IconButton>

                  <Typography
                    variant="body2"
                    className={
                      classes[
                        nftInfo.isFinished ? "dangerStatus" : "successStatus"
                      ]
                    }
                  >
                    {mainLoading ? (
                      <Skeleton width={80} />
                    ) : (
                      <>{nftInfo.isFinished ? "Redeamed" : "Trading"}</>
                    )}
                  </Typography>

                  <Typography variant="h4" className="fw-bold my-2">
                    {mainLoading ? (
                      <Skeleton width={80} />
                    ) : (
                      <>{nftInfo?.nftInfo?.name || nftInfo.nftName}</>
                    )}
                  </Typography>
                  <Typography variant="body1" className="mb-4">
                    {mainLoading ? (
                      <Skeleton width={50} />
                    ) : (
                      <>#{nftInfo.nftId}</>
                    )}
                  </Typography>
                  {mainLoading && (
                    <Typography variant="body1" className="mb-4">
                      <Skeleton />
                      <Skeleton />
                    </Typography>
                  )}

                  {nftInfo?.nftInfo?.description && (
                    <Typography variant="body1" className="mb-4">
                      {nftInfo?.nftInfo?.description}
                    </Typography>
                  )}

                  <Typography variant="body1">
                    {mainLoading ? (
                      <Skeleton width={100} />
                    ) : (
                      <>
                        <a
                          href={convertExploreLink(network.chainID, contract)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.moreInfo}
                        >
                          Contract Explorer
                          <CallMadeIcon className={classes.exploreIcon} />
                        </a>
                      </>
                    )}
                  </Typography>

                  <Typography variant="body1" className="mb-4">
                    {mainLoading ? (
                      <Skeleton width={100} />
                    ) : (
                      <>
                        <a
                          href={convertExploreLink(
                            network.chainID,
                            nftInfo.nftAddress
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.moreInfo}
                        >
                          NFT Explorer
                          <CallMadeIcon className={classes.exploreIcon} />
                        </a>
                      </>
                    )}
                  </Typography>

                  <Typography variant="body2" className="mb-2">
                    {mainLoading ? (
                      <Skeleton width={50} />
                    ) : (
                      <>
                        1 NFT Share ={" "}
                        {Number(nftInfo.finalNftEthPrice / 1000000)
                          .toFixed(18)
                          .replace(/\.?0+$/, "")}{" "}
                        Matic
                      </>
                    )}
                  </Typography>
                  <Typography variant="body2" className="mb-2">
                    1.000.000 NFT Shares
                  </Typography>
                  <Typography variant="body2" className="mb-2">
                    {mainLoading ? (
                      <Skeleton width={50} />
                    ) : (
                      <>{nftInfo.finalNftEthPrice} Matic</>
                    )}
                  </Typography>
                </div>

                <div className="d-flex flex-column align-items-start mt-5">
                  <div className={classes.infoLoadingBTNBox}>
                    <LoadingButton
                      loading={loading}
                      valid={!mainLoading}
                      success={false}
                      fail={false}
                      onClick={nftInfo.isFinished ? redeem : buyNFT}
                    >
                      {loading
                        ? "processing"
                        : `${nftInfo.isFinished ? "redeem" : "buy"}`}
                    </LoadingButton>
                  </div>

                  {!mainLoading && nftInfo.isFinished && (
                    <Typography variant="caption">
                      {nftInfo.balance} NFT Share ≃ {nftInfo.maticBalance} Matic
                    </Typography>
                  )}
                </div>
              </div>

              <div className={classes.imageBox}>
                {mainLoading || !nftInfo?.nftInfo?.image ? (
                  <Skeleton
                    variant="rect"
                    height="100%"
                    className={classes.skeletonImage}
                  />
                ) : (
                  <img
                    src={nftInfo?.nftInfo?.image}
                    alt={nftInfo?.nftInfo?.name || nftInfo.nftName}
                    className={classes.nftImage}
                  />
                )}
              </div>
            </Paper>
          </Container>
        </div>
      ) : (
        <Redirect to="/invest" />
      )}
    </>
  );
}

export default NFTPoolDetail;
